import _defineProperty from "@babel/runtime/helpers/defineProperty";
// For some wacky reason the linter thinks I am not using anchorItem!!!
// @ts-ignore: noUnusedLocals

/**
 * Model and controller class for managing selection in a DataGrid component.
 * This class manages the logic to support the various selectionMode types, and
 * utilizes the DataGridSelectionMachine to do most of the work.
 * Use onSelectionChange() callback to get notified when the selection changes.
 */
export class DataGridSelectionMgr {
  constructor(dataGrid, selectionMachine) {
    _defineProperty(this, "dataGrid", void 0);
    _defineProperty(this, "_selectionMachine", void 0);
    // Callback when the selection changes.
    _defineProperty(this, "onSelectionChange", void 0);
    _defineProperty(this, "onFocusCellChange", void 0);
    this.dataGrid = dataGrid;
    this._selectionMachine = selectionMachine;
    this.selectionMachine = selectionMachine;
  }
  get selectionMachine() {
    return this._selectionMachine;
  }
  set selectionMachine(value) {
    if (this._selectionMachine != null) {
      let sm = this._selectionMachine;
      sm.dataGrid = undefined;
      sm.onSelectionChangePrivateCallback = undefined;
      sm.onFocusCellChangePrivateCallback = undefined;
    }
    this._selectionMachine = value;
    if (this._selectionMachine != null) {
      let sm = this._selectionMachine;
      sm.dataGrid = this.dataGrid;
      sm.onSelectionChangePrivateCallback = this.onSelectionChangeInternal.bind(this);
      sm.onFocusCellChangePrivateCallback = this.onFocusCellChangeInternal.bind(this);
    }
  }
  get isSingleSelectionMode() {
    return this.selectionMachine.isSingleSelectionMode;
  }
  get isMultipleSelectionMode() {
    return this.selectionMachine.isMultipleSelectionMode;
  }
  get isRectangularSelectionMode() {
    return this.selectionMachine.isRectangularSelectionMode;
  }
  get isRowSelectionMode() {
    return this.selectionMachine.isRowSelectionMode;
  }
  get isCellSelectionMode() {
    return this.selectionMachine.isCellSelectionMode;
  }
  get canSelect() {
    return this.selectionMachine.canSelect;
  }
  get canSelectCells() {
    return this.selectionMachine.canSelectCells;
  }
  get canSelectColumns() {
    return this.selectionMachine.canSelectColumns;
  }

  /**
   * Clears the selection region.
   */
  clear() {
    return this.selectionMachine.clear();
  }

  /**
   * Selects a row.
   * @param {RowId} rowId
   * @param {boolean} addToSelection if true, the row is added to the selection,
   * if false, any current selection is cleared.
   */
  selectRow(rowId, addToSelection) {
    this.selectionMachine.selectRow(rowId, addToSelection);
  }
  deselectRow(rowId) {
    this.selectionMachine.deselectRow(rowId);
  }
  selectCell(rowId, colId, addToSelection, isFocusCell) {
    return this.selectionMachine.selectCell(rowId, colId, addToSelection, isFocusCell);
  }
  deselectCell(rowId, colId) {
    this.selectionMachine.deselectCell(rowId, colId);
  }

  /**
   * Select a column.
   * @param {ColId} colId
   * @param {boolean} addToSelection addToSelection if true, the row is added to the selection,
   * if false, any current selection is cleared.
   */
  selectColumn(colId, addToSelection) {
    this.selectionMachine.selectColumn(colId, addToSelection);
  }
  deselectColumn(colId) {
    this.selectionMachine.deselectColumn(colId);
  }

  /**
   * User action when clicked on a cell body cell.
   * @param {RowId} rowId
   * @param {string} colId
   * @param {boolean} shiftKey
   * @param {boolean} ctrlKey
   */
  onClickCell(rowId, colId, shiftKey, ctrlKey) {
    if (!this.canSelect) {
      return;
    }
    if (this.isRowSelectionMode) {
      // Delegate to row support
      this.onClickRowHeader(rowId, shiftKey, ctrlKey);
      return;
    }

    // We are in cell select mode
    if (ctrlKey && this.isCellSelected(rowId, colId)) {
      this.selectionMachine.onCtrlClickSelectedCell(rowId, colId);
    } else if (shiftKey && this.anchorItem && this.anchorItem.isCell) {
      this.extendCellSelection(rowId, colId);
    } else {
      this.selectCell(rowId, colId, ctrlKey, true);
    }
  }

  /**
   * User action when clicked on a row header cell.
   * @param {RowId} rowId
   * @param {boolean} shiftKey
   * @param {boolean} ctrlKey
   */
  onClickRowHeader(rowId, shiftKey, ctrlKey) {
    let isSelected = this.isRowSelected(rowId);
    if (isSelected && ctrlKey) {
      this.selectionMachine.onCtrlClickSelectedRow(rowId);
    } else if (shiftKey && this.anchorItem && this.anchorItem.rowId != null) {
      this.extendRowSelection(rowId);
    } else {
      this.selectRow(rowId, ctrlKey);
    }
  }

  /**
   * User action when clicked on a column header cell.
   * @param {string} colId
   * @param {boolean} shiftKey
   * @param {boolean} ctrlKey
   */
  onClickColumnHeader(colId, shiftKey, ctrlKey) {
    if (!this.canSelectColumns) {
      return;
    }
    let isSelected = this.isColumnSelected(colId);
    if (isSelected && ctrlKey) {
      this.selectionMachine.onCtrlClickSelectedColumn(colId);
    } else if (shiftKey && this.anchorItem && this.anchorItem.isColumn) {
      this.extendColumnSelection(colId);
    } else {
      this.selectColumn(colId, ctrlKey);
    }
  }

  /**
   * Determines if cell is selected either explicitly or via row or column selection.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @returns {boolean}
   */
  isSelected(rowId, colId) {
    return this.selectionMachine.isSelected(rowId, colId);
  }

  /**
   * Determines if cell is selected explicitly as a selected cell.
   * @param {RowId} rowId
   * @param {ColId} colId
   * @returns {boolean}
   */
  isCellSelected(rowId, colId) {
    return this.selectionMachine.isCellSelected(rowId, colId);
  }
  isFocusCell(rowId, colId) {
    return this.selectionMachine.isFocusCell(rowId, colId);
  }
  isRowSelected(rowId) {
    return this.selectionMachine.isRowSelected(rowId);
  }
  isColumnSelected(colId) {
    return this.selectionMachine.isColumnSelected(colId);
  }
  columnHasSelectedCells(colId) {
    return this.selectionMachine.columnHasSelectedCells(colId);
  }
  onSelectionChangeInternal() {
    // console.log(`onSelectionChangeInternal`);

    if (this.onSelectionChange) {
      this.onSelectionChange();
    }
  }
  onFocusCellChangeInternal() {
    if (this.onFocusCellChange) {
      this.onFocusCellChange();
    }
  }
  get focusCell() {
    let sm = this.selectionMachine;
    return sm.selectionRegion.focusCell;
  }
  get anchorItem() {
    let sm = this.selectionMachine;
    return sm.selectionRegion.anchorItem;
  }
  get selectionRegion() {
    let sm = this.selectionMachine;
    return sm.selectionRegion;
  }
  extendRowSelection(rowId3) {
    this.selectionMachine.extendRowSelection(rowId3);
  }
  extendCellSelection(rowId, colId) {
    this.selectionMachine.extendCellSelection(rowId, colId);
  }
  extendColumnSelection(colId) {
    this.selectionMachine.extendColumnSelection(colId);
  }
  selectAll() {
    this.selectionMachine.selectAll();
  }

  /**
   * Returns array of SelectedItems.
   * @returns {SelectedItem[]}
   */
  getSelectedItems() {
    return this.selectionMachine.getSelectedItems();
  }
}
//# sourceMappingURL=DataGridSelectionMgr.js.map
